import { toSearchParams } from '@shigo-ui/api-client';

import type { DashboardDateRange } from '@/components/dashboard/model';
import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import { publisherDashboardReportSchema } from './model';
import {
  networkDashboardOverviewSchema,
  networkDemandSourcesSchema,
  type NetworkDashboardOverview,
  type NetworkDemandSources,
} from '../../admin/networks/dashboard/model';

interface PublisherDashboardClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}
export function createPublisherDashboardClient({
  config,
  accessToken,
  fetcher,
}: PublisherDashboardClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  function detailsReportPath(
    resource: 'report' | 'report-publisher-top5-dp',
    publisherId: string,
    range: DashboardDateRange,
  ) {
    const base = `/api/dashboard/${resource}/${encodeURIComponent(publisherId)}`;
    if (range.preset) {
      const search = new URLSearchParams({
        presetDateRange: range.preset,
        offsetCount: '0',
      });
      return `${base}/${resource === 'report' ? 'overview-presets' : 'presets'}?${search.toString()}`;
    }
    return `${base}?${rangeSearch(range)}`;
  }

  return {
    report(publisherId: string, range: DashboardDateRange, signal?: AbortSignal) {
      return api.request({
        path: `/api/pub-dash/dashboard/${encodeURIComponent(publisherId)}?${rangeSearch(range)}`,
        schema: publisherDashboardReportSchema,
        ...(signal ? { init: { signal } } : {}),
      });
    },
    detailsOverview(
      publisherId: string,
      range: DashboardDateRange,
      signal?: AbortSignal,
    ): Promise<NetworkDashboardOverview> {
      return api.request({
        path: detailsReportPath('report', publisherId, range),
        schema: networkDashboardOverviewSchema,
        ...(signal ? { init: { signal } } : {}),
      });
    },
    detailsDemandSources(
      publisherId: string,
      range: DashboardDateRange,
      signal?: AbortSignal,
    ): Promise<NetworkDemandSources> {
      return api.request({
        path: detailsReportPath('report-publisher-top5-dp', publisherId, range),
        schema: networkDemandSourcesSchema,
        ...(signal ? { init: { signal } } : {}),
      });
    },
  };
}

function rangeSearch(range: DashboardDateRange) {
  return toSearchParams({ dateFrom: range.dateFrom, dateTo: range.dateTo });
}
